import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, eur } from "@/lib/api";
import { addItem, readCart } from "@/lib/cart";
import { toast } from "sonner";
import { ArrowLeft, Award, Grape, Thermometer, MapPin, Wine as WineIcon, Sparkles } from "lucide-react";

/**
 * Fase 19 — Carta dei Vini Premium.
 * Pagina prodotto vino con layout editoriale ispirato a sarabibeach.com.
 * Priorità: raccontare il vino, non vendere. CTA discreta.
 * Ogni sezione appare solo se il dato è presente (nessuno spazio vuoto).
 */

// Field lists ordered by editorial priority (not database order).
const IDENTITY_FIELDS = [
  ["denominazione", "Denominazione"],
  ["tipologia", "Tipologia"],
  ["wine_type", "Tipo di vino"],
  ["vitigno", "Vitigno"],
];
const ORIGIN_FIELDS = [
  ["regione", "Regione"],
  ["provenienza", "Provenienza"],
  ["zona", "Zona di produzione"],
  ["country", "Paese"],
];
const SERVICE_FIELDS = [
  ["gradazione", "Gradazione"],
  ["temperatura_servizio", "Temperatura di servizio"],
  ["formato", "Formato"],
  ["affinamento", "Affinamento"],
];
const TASTING_FIELDS = [
  ["colore", "Colore"],
  ["profumo", "Profumo"],
  ["sapore", "Sapore"],
  ["momento_ideale", "Momento ideale"],
];

const looksLikePlaceholder = (s) => {
  const v = (s || "").trim().toLowerCase();
  if (v.length < 15) return true;
  return ["prova", "test", "lorem", "todo", "placeholder", "descrizione"].includes(v);
};

function Section({ children, className = "" }) {
  return <section className={`mt-10 md:mt-12 ${className}`}>{children}</section>;
}

function SectionTitle({ children, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-3.5 h-3.5 text-bronze-600" />}
      <div className="uppercase text-[10px] tracking-[0.28em] text-bronze-700 font-medium">
        {children}
      </div>
      <div className="flex-1 h-px bg-sand-300/70" />
    </div>
  );
}

function DataRow({ label, value, testid }) {
  if (!value || !String(value).trim()) return null;
  return (
    <div className="py-2.5 border-b border-sand-200 last:border-b-0 grid grid-cols-[10rem_1fr] gap-4 md:gap-6"
         data-testid={testid}>
      <dt className="uppercase text-[10px] tracking-[0.22em] text-ink-500 pt-0.5">{label}</dt>
      <dd className="text-ink-800 font-medium leading-relaxed">{value}</dd>
    </div>
  );
}

function Narrative({ text, testid }) {
  if (!text || looksLikePlaceholder(text)) return null;
  return (
    <p className="font-serif text-lg md:text-xl leading-[1.7] text-ink-800 italic" data-testid={testid}>
      {text}
    </p>
  );
}

export default function WineDetail() {
  const { productId } = useParams();
  const nav = useNavigate();
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    api.get("/public/menu").then((r) => setMenu(r.data));
    if (!readCart().station) nav("/");
  }, [nav]);

  const { product, cat, root, calice } = useMemo(() => {
    if (!menu) return { product: null, cat: null, root: null, calice: null };
    const p = menu.products.find((x) => x.id === productId);
    if (!p) return { product: null, cat: null, root: null, calice: null };
    const c = menu.categories.find((x) => x.id === p.category_id) || null;
    const r = c && c.parent_id ? menu.categories.find((x) => x.id === c.parent_id) || null : null;
    const nrm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
    const npn = nrm(p.name);
    let caliceMatch = null;
    if (p.wine && npn) {
      const caliceCats = menu.categories.filter((x) => nrm(x.slug || x.name).includes("calice"));
      const caliceCatIds = new Set(caliceCats.map((x) => x.id));
      caliceMatch = menu.products.find((x) => {
        if (x.id === p.id) return false;
        if (!caliceCatIds.has(x.category_id)) return false;
        const nx = nrm(x.name);
        if (!nx) return false;
        return nx === npn || nx.includes(npn) || npn.includes(nx);
      }) || null;
    }
    return { product: p, cat: c, root: r, calice: caliceMatch };
  }, [menu, productId]);

  if (!menu) return <div className="p-10 text-ink-500 font-serif italic">Caricamento…</div>;
  if (!product) {
    return (
      <div className="p-10 text-center max-w-md mx-auto">
        <div className="font-serif text-3xl mb-3">Vino non disponibile</div>
        <p className="text-sm text-ink-500 mb-6">
          Il vino richiesto non è più nella carta o è stato aggiornato.
        </p>
        <button data-testid="wine-back-to-menu" onClick={() => nav("/menu")}
                className="text-bronze-700 hover:text-bronze-900 uppercase text-xs tracking-[0.2em]">
          ← Torna alla carta
        </button>
      </div>
    );
  }

  const wine = product.wine || {};
  const badges = product.badges || [];
  const available = product.available !== false;
  const featured = !!wine.featured;
  const showPrice = wine.show_price !== false;

  const add = () => {
    if (!available || !showPrice) return;
    addItem(product, 1, [], null);
    toast.success(`${product.name} aggiunto`);
  };

  const hasIdentity = IDENTITY_FIELDS.some(([k]) => wine[k]);
  const hasOrigin   = ORIGIN_FIELDS.some(([k]) => wine[k]);
  const hasService  = SERVICE_FIELDS.some(([k]) => wine[k]);
  const hasTasting  = TASTING_FIELDS.some(([k]) => wine[k]);
  const notes = wine.note && !looksLikePlaceholder(wine.note) ? wine.note : null;
  const abbConsigliati = wine.abbinamenti_consigliati || wine.abbinamenti;
  const description = !looksLikePlaceholder(product.description) ? product.description : null;

  return (
    <div className="min-h-screen pb-40 bg-[#faf7f2]" data-testid="wine-detail-page">
      {/* Silent breadcrumb — small, elegant, not clickable except back */}
      <header className="sticky top-0 z-20 bg-[#faf7f2]/95 backdrop-blur-xl border-b border-sand-200/70 px-5 md:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button data-testid="wine-back" onClick={() => nav(-1)}
                  className="text-bronze-700 hover:text-bronze-900 flex items-center gap-1.5 uppercase text-[11px] tracking-[0.2em]">
            <ArrowLeft className="w-3.5 h-3.5" /> Carta dei Vini
          </button>
          {root && cat && (
            <nav className="hidden md:block text-[11px] text-ink-500 uppercase tracking-[0.18em]"
                 data-testid="wine-breadcrumb">
              {root.name} <span className="mx-1.5 text-ink-300">·</span> {cat.name}
            </nav>
          )}
        </div>
      </header>

      <main className="px-5 md:px-8 pt-8 md:pt-16 pb-10 max-w-4xl mx-auto md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] md:gap-14 md:items-start">

        {/* HERO: bottle photo */}
        <div className="md:sticky md:top-24">
          <div className="relative">
            {product.image_url ? (
              <img data-testid="wine-photo"
                   src={product.image_url}
                   alt={`${wine.producer || ""} ${product.name}`.trim()}
                   loading="eager"
                   decoding="async"
                   className="w-full max-w-sm mx-auto rounded-sm object-contain aspect-[3/4.4] bg-white shadow-[0_25px_60px_-25px_rgba(90,60,30,0.35)]" />
            ) : (
              <div data-testid="wine-photo-placeholder"
                   className="w-full max-w-sm mx-auto rounded-sm bg-white flex items-center justify-center aspect-[3/4.4] text-ink-300 font-serif text-4xl italic tracking-widest shadow-[0_25px_60px_-25px_rgba(90,60,30,0.25)]">
                Sarabi
              </div>
            )}
            {/* Featured ribbon */}
            {featured && (
              <div className="absolute top-3 -left-2 rotate-[-4deg] bg-bronze-700 text-sand-50 text-[9px] uppercase tracking-[0.22em] px-3 py-1.5 shadow-md"
                   data-testid="wine-featured-ribbon">
                <Sparkles className="w-3 h-3 inline-block mr-1 -mt-0.5" /> Selezione Sarabi
              </div>
            )}
          </div>

          {/* Calice pill */}
          {product.wine && (
            <div className="mt-6 max-w-sm mx-auto text-center">
              {calice ? (
                <div className="text-[11px] uppercase tracking-[0.22em] text-bronze-800"
                     data-testid="wine-calice-available">
                  <WineIcon className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5 text-bronze-700" />
                  Anche al calice · <b className="font-serif normal-case text-base tracking-normal">{eur(calice.price_cents)}</b>
                </div>
              ) : (
                <div className="text-[10px] uppercase tracking-[0.22em] text-ink-400"
                     data-testid="wine-calice-bottle-only">
                  Solo bottiglia
                </div>
              )}
            </div>
          )}
        </div>

        {/* EDITORIAL BLOCK */}
        <article className="mt-10 md:mt-0">
          {wine.producer && (
            <div className="uppercase tracking-[0.28em] text-[11px] text-bronze-700 font-medium mb-3"
                 data-testid="wine-producer">
              {wine.producer}
            </div>
          )}
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] text-ink-900" data-testid="wine-name">
            {product.name}
          </h1>

          {/* Sub-title (regione/provenienza) */}
          {(wine.regione || wine.provenienza) && (
            <div className="mt-3 flex items-center gap-1.5 text-sm text-ink-500 italic">
              <MapPin className="w-3.5 h-3.5" />
              {wine.regione || wine.provenienza}
              {wine.country && wine.country !== (wine.regione || wine.provenienza) && (
                <span> · {wine.country}</span>
              )}
            </div>
          )}

          {/* Badges */}
          {badges.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5" data-testid="wine-badges">
              {badges.map((b) => (
                <span key={b} data-testid={`wine-badge-${b}`}
                      className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 border border-bronze-500/50 text-bronze-700 bg-bronze-50/40 rounded-sm">
                  {b}
                </span>
              ))}
            </div>
          )}

          {/* Price (elegant, no big CTA) */}
          <div className="mt-6 flex items-baseline gap-4">
            {showPrice ? (
              <div className="font-serif text-3xl text-ink-900" data-testid="wine-price">
                {eur(product.price_cents)}
              </div>
            ) : (
              <div className="italic text-ink-500 text-lg" data-testid="wine-price-on-request">
                Prezzo su richiesta al nostro sommelier
              </div>
            )}
            {!available && (
              <span data-testid="wine-out-of-stock"
                    className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-red-300 text-red-700 bg-red-50 rounded-sm">
                Esaurito
              </span>
            )}
          </div>

          {/* Narrative description */}
          {description && (
            <Section className="mt-8 md:mt-10">
              <Narrative text={description} testid="wine-description" />
            </Section>
          )}

          {/* Identity */}
          {hasIdentity && (
            <Section>
              <SectionTitle icon={Grape}>Il vino</SectionTitle>
              <dl>
                {IDENTITY_FIELDS.map(([k, l]) => (
                  <DataRow key={k} label={l} value={wine[k]} testid={`wine-field-${k}`} />
                ))}
              </dl>
            </Section>
          )}

          {/* Origin */}
          {hasOrigin && (
            <Section>
              <SectionTitle icon={MapPin}>Territorio</SectionTitle>
              <dl>
                {ORIGIN_FIELDS.map(([k, l]) => (
                  <DataRow key={k} label={l} value={wine[k]} testid={`wine-field-${k}`} />
                ))}
              </dl>
            </Section>
          )}

          {/* Tasting notes */}
          {(hasTasting || notes) && (
            <Section>
              <SectionTitle>Note degustative</SectionTitle>
              {notes && (
                <p className="font-serif text-base md:text-lg leading-[1.75] text-ink-700 italic mb-5"
                   data-testid="wine-tasting-notes">
                  {notes}
                </p>
              )}
              {hasTasting && (
                <dl>
                  {TASTING_FIELDS.map(([k, l]) => (
                    <DataRow key={k} label={l} value={wine[k]} testid={`wine-tasting-${k}`} />
                  ))}
                </dl>
              )}
            </Section>
          )}

          {/* Service */}
          {hasService && (
            <Section>
              <SectionTitle icon={Thermometer}>Servizio</SectionTitle>
              <dl>
                {SERVICE_FIELDS.map(([k, l]) => (
                  <DataRow key={k} label={l} value={wine[k]} testid={`wine-field-${k}`} />
                ))}
              </dl>
            </Section>
          )}

          {/* Abbinamenti */}
          {abbConsigliati && !looksLikePlaceholder(abbConsigliati) && (
            <Section>
              <SectionTitle>Abbinamenti consigliati</SectionTitle>
              <p className="font-serif text-lg leading-[1.7] text-ink-800 italic"
                 data-testid="wine-abbinamenti">
                {abbConsigliati}
              </p>
            </Section>
          )}

          {/* Premi */}
          {wine.premi && (
            <Section>
              <SectionTitle icon={Award}>Riconoscimenti</SectionTitle>
              <p className="text-ink-800 leading-relaxed" data-testid="wine-premi">
                {wine.premi}
              </p>
            </Section>
          )}

          {/* Extra generic pairs */}
          {Object.entries(wine.extra || {}).filter(([k, v]) =>
            (k || "").trim() && (v || "").toString().trim()).length > 0 && (
            <Section>
              <SectionTitle>Dettagli</SectionTitle>
              <dl>
                {Object.entries(wine.extra || {}).map(([k, v]) =>
                  (k || "").trim() && (v || "").toString().trim() ? (
                    <DataRow key={`extra-${k}`}
                             label={k.replace(/_/g, " ")}
                             value={v}
                             testid={`wine-extra-${k}`} />
                  ) : null,
                )}
              </dl>
            </Section>
          )}
        </article>
      </main>

      {/* Discreet sticky CTA — never dominates */}
      {showPrice && available && (
        <div className="fixed bottom-0 left-0 right-0 z-30 px-5 py-4 bg-[#faf7f2]/95 backdrop-blur-lg border-t border-sand-200"
             data-testid="wine-sticky-bar">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-ink-400">
                {wine.producer || product.name}
              </div>
              <div className="font-serif text-xl text-ink-900 truncate">
                {eur(product.price_cents)}
              </div>
            </div>
            <button data-testid="wine-add-to-cart-btn"
                    onClick={add}
                    className="uppercase tracking-[0.22em] text-[11px] font-medium border border-bronze-700 text-bronze-700 hover:bg-bronze-700 hover:text-sand-50 transition-colors px-6 py-3 rounded-sm">
              Aggiungi
            </button>
          </div>
        </div>
      )}
      {!available && showPrice && (
        <div className="fixed bottom-0 left-0 right-0 z-30 px-5 py-3 bg-red-50 border-t border-red-200 text-center text-[11px] uppercase tracking-[0.22em] text-red-700"
             data-testid="wine-sticky-out-of-stock">
          Bottiglia esaurita
        </div>
      )}
    </div>
  );
}
